import { propertyConfig } from "../constants/ruleProperties";
import { getSegmentedRule, parseLineToRow, RuleRow } from "./parseRules";

const INITIAL_DELIMITERS = ["is", "is for any", "="];

// Aliases that introduce an applicability group like `for (A|B|C)` — used to gate
// group-shape validation so it doesn't false-positive on regex/PowerShell values.
const APPLICABILITY_GROUP_ALIASES = new Set([
  "for", "for a", "for any",
  "not for", "not for a", "not for any",
  "and for", "and not for", "or for", "or not for",
]);

const validateApplicabilityGroup = (row: RuleRow, index: number): string[] => {
  const errors: string[] = [];
  const value = (row.value ?? "").trim();
  if (!value.startsWith("(") || !value.endsWith(")")) return errors;

  const inner = value.slice(1, -1);

  if (inner.trim() === "") {
    errors.push(`Row ${index + 1}: '${row.alias}' has an empty applicability list.`);
    return errors;
  }

  if (inner.includes('"')) {
    errors.push(`Row ${index + 1}: unexpected '"' inside '${row.alias} (${inner})' — did you mean '|'?`);
  }

  const tokens = inner.split("|").map((t) => t.trim());
  if (tokens.some((t) => t === "")) {
    errors.push(`Row ${index + 1}: '${row.alias} (${inner})' has an empty token — check for a stray '|' at the start, end, or duplicated.`);
  }

  return errors;
};

// Validate the initial delimiter
const validateInitialDelimiter = (rules: RuleRow[]): string | null => {
  const firstDelimiter = rules[0]?.alias;
  return firstDelimiter && INITIAL_DELIMITERS.includes(firstDelimiter)
    ? null
    : `The rule must start with one of: ${INITIAL_DELIMITERS.join(", ")}.`;
};

// Check for repeated initial delimiters
const validateNoRepeatedInitialDelimiters = (rules: RuleRow[]): string[] =>
  rules
    .slice(1)
    .map((row, i) =>
      INITIAL_DELIMITERS.includes(row.alias) ? `Delimiter '${row.alias}' can only be used at the start (row ${i + 2}).` : null
    )
    .filter(Boolean) as string[];

// Validate an individual row
const validateRow = (row: RuleRow, index: number): string[] => {
  const errors: string[] = [];

  if (!row.alias) {
    return [`Row ${index + 1}: Missing delimiter.`];
  }

  if (!row.property && !row.value) {
    return [`Row ${index + 1}: Must have a property or value after '${row.alias}'.`];
  }

  if (row.property) {
    const config = propertyConfig[row.property];
    if (!config) {
      errors.push(`Row ${index + 1}: Invalid property '${row.property}'.`);
    } else {
      
      // Validate regex if defined for the property
      if (config.regex && row.value) {
        const isEnumRegex =
          config.regex.includes('|') &&
          !config.regex.includes('[') &&
          !config.regex.includes('\\');

        if (isEnumRegex) {
          // enum-style regex: ^(?:x86|x64|ARM64)$
          const allowedValues = config.regex
            .replace(/^\^\(\?:|\)\$$/g, '')
            .split('|');

          if (!allowedValues.includes(row.value)) {
            errors.push(
               `Row ${index + 1}: '${row.property}' must use a value from the dropdown. Please select one from the dropdown.`
            );
          }
        } else {
          // normal regex validation
          try {
            const pattern = new RegExp(config.regex);
            if (!pattern.test(row.value)) {
              errors.push(
                `Row ${index + 1}: Value '${row.value}' does not match the required pattern for '${row.property}': ${config.regex}`
              );
            }
          } catch {
            errors.push(
              `Row ${index + 1}: Invalid regex pattern for '${row.property}': ${config.regex}`
            );
          }
        }
      }

      if (config.operators.length > 0) {
        if (!row.operator) {
          errors.push(`Row ${index + 1}: '${row.property}' requires an operator.`);
        } else if (!config.operators.includes(row.operator)) {
          errors.push(`Row ${index + 1}: Operator '${row.operator}' is not valid for '${row.property}'.`);
        } else if (row.operator && !row.value) {
          errors.push(`Row ${index + 1}: Operator '${row.operator}' requires a value.`);
        }
      } else if (row.operator || row.value) {
        errors.push(`Row ${index + 1}: '${row.property}' does not allow operators or values.`);
      }
    }
  } else if (!row.value) {
    errors.push(`Row ${index + 1}: '${row.alias}' requires a value if no property is present.`);
  }

  if (APPLICABILITY_GROUP_ALIASES.has(row.alias)) {
    errors.push(...validateApplicabilityGroup(row, index));
  }

  return errors;
};

// Main validation function
export const validateRule = (rules: RuleRow[]): { isValid: boolean; errors: string[] } => {
  const errors = [
    validateInitialDelimiter(rules),
    ...validateNoRepeatedInitialDelimiters(rules),
    ...rules.flatMap((row, index) => validateRow(row, index)),
  ].filter(Boolean) as string[];

  return { isValid: errors.length === 0, errors };
};


// Delegates to parseLineToRow + getSegmentedRule + validateRule so the check
// respects the same delimiter/property/regex constants the rest of the editor
// already uses. Returning `boolean` keeps the existing callers unchanged.
//
// `allowEmptyName` matches the AidenBot parser's behaviour for AD Domain
// ("leave blank to skip the domain join") and AD OU root entries where the
// name is optional and the line only has the expression on the right.
export const isValidRule = (content: string, options: { allowEmptyName?: boolean } = {}): boolean => {
  const parsed = parseLineToRow(content, options);
  if (!parsed) return false;

  const rest = `${parsed.delimiter} ${parsed.value}`;
  const segments = getSegmentedRule(rest);
  if (segments.length === 0) return false;

  const rows: RuleRow[] = segments.map((s) => ({
    delimiter: s.delimiter,
    alias: s.alias,
    property: s.property,
    operator: s.operator,
    value: s.value,
  }));
  return validateRule(rows).isValid;
};